import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import ModalDialog from '../../../core/components/Modal';
import { hideDialog } from '../../../core/actions';
import { selectOperatorGroup, selectSite } from '../../../store/authReducer';
import { selectCoreSiteId } from '../../settings/reducer';
import {
  fetchNotifications as fetchNotificationsAction,
  fetchFollowUpNotifications as fetchFollowUpNotificationsAction,
  fetchStalledClaimNotifications as fetchStalledClaimNotificationsAction,
  fetchClaimEmailApprovalNotifications as fetchClaimEmailApprovalNotificationsAction,
  fetchClaimCreatedNotifications as fetchClaimCreatedNotificationsAction,
  markNotificationRead,
  CLAIM_EMAIL_APPROVAL_NOTIFICATION_TYPE,
  CLAIM_CREATED_NOTIFICATION_TYPE,
  FOLLOW_UP_NOTIFICATION_TYPE,
  STALLED_CLAIM_NOTIFICATION_TYPE,
  WHATS_NEW_NOTIFICATION_TYPE,
} from '../actions';
import { parseNotificationBody, normalizeExternalLink, sanitizeHtmlContent } from '../utils';
import { extractClaimId, getNotificationSiteId } from './claimNotificationHelpers';
import { ClaimNotificationActions } from './ClaimNotificationActions';

interface WhatsNewNotificationDialogProps {
  isOpen: boolean;
  notification: any;
  notificationQueue?: any[];
  onClose?: (notificationId?: number) => void;
}

const WhatsNewNotificationDialog = ({
  isOpen,
  notification,
  notificationQueue,
  onClose,
}: WhatsNewNotificationDialogProps) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const history = useHistory();
  const siteId = useSelector(selectSite);
  const coreSiteId = useSelector(selectCoreSiteId);
  const operatorGroup = useSelector(selectOperatorGroup);
  const hasMarkedRead = useRef(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [queueSnapshot, setQueueSnapshot] = useState<any[]>([]);

  const fallbackQueue = useMemo(() => {
    if (Array.isArray(notificationQueue) && notificationQueue.length) {
      return notificationQueue;
    }
    return notification ? [notification] : [];
  }, [notification, notificationQueue]);

  const queue = queueSnapshot.length ? queueSnapshot : fallbackQueue;

  const queueKey = useMemo(() => queue.map((item) => item?.id).join('-'), [queue]);
  const currentNotification = queue[currentIndex] || null;
  const totalNotifications = queue.length;
  const hasMultiple = totalNotifications > 1;
  const isLast = currentIndex >= totalNotifications - 1;
  const bodyContent = useMemo(
    () => parseNotificationBody(currentNotification?.body),
    [currentNotification?.body]
  );
  const notificationMedia = Array.isArray(currentNotification?.media)
    ? currentNotification.media
    : [];
  const imageMedia = notificationMedia.find(
    (entry: any) => typeof entry?.mimeType === 'string' && entry.mimeType.startsWith('image')
  );
  const videoMedia = notificationMedia.find(
    (entry: any) => typeof entry?.mimeType === 'string' && entry.mimeType.startsWith('video')
  );
  const imageSrc =
    imageMedia?.downloadUrl || imageMedia?.url || bodyContent.imageData || bodyContent.imageUrl;
  const videoSrc =
    videoMedia?.downloadUrl || videoMedia?.url || bodyContent.videoData || bodyContent.videoUrl;
  const rawMessage =
    bodyContent.message ||
    (typeof currentNotification?.body === 'string' ? currentNotification.body : '');
  const notificationType = useMemo(() => {
    const typeValue =
      currentNotification?.type ||
      currentNotification?.notificationType ||
      currentNotification?.category;
    return typeof typeValue === 'string' ? typeValue.toLowerCase() : '';
  }, [currentNotification]);
  const followUpMessageParts = useMemo(() => {
    if (notificationType !== FOLLOW_UP_NOTIFICATION_TYPE) {
      return { message: rawMessage, note: '' };
    }

    const noteMatch = rawMessage.match(/([\s\S]*?)(?:\s|&nbsp;|<br\s*\/?>)*\bNote:\s*([\s\S]*)/i);
    if (!noteMatch) {
      return { message: rawMessage, note: '' };
    }

    return {
      message: noteMatch[1].trim(),
      note: noteMatch[2].trim(),
    };
  }, [notificationType, rawMessage]);
  const messageHtml = sanitizeHtmlContent(followUpMessageParts.message);
  const followUpNotes = useMemo(() => {
    if (notificationType !== FOLLOW_UP_NOTIFICATION_TYPE) {
      return '';
    }

    const noteValue =
      currentNotification?.notes ||
      currentNotification?.note ||
      currentNotification?.data?.notes ||
      currentNotification?.data?.note ||
      currentNotification?.meta?.notes ||
      currentNotification?.meta?.note ||
      currentNotification?.details?.notes ||
      currentNotification?.details?.note ||
      (bodyContent as any)?.notes ||
      (bodyContent as any)?.note;

    if (Array.isArray(noteValue)) {
      return noteValue
        .map((note) => (typeof note === 'string' ? note : note?.text || note?.body || note?.note))
        .filter(Boolean)
        .join('<br />');
    }

    return typeof noteValue === 'string' ? noteValue : followUpMessageParts.note;
  }, [currentNotification, bodyContent, notificationType, followUpMessageParts.note]);
  const followUpNotesHtml = sanitizeHtmlContent(followUpNotes);
  const linkUrl = useMemo(
    () => normalizeExternalLink(currentNotification?.link || bodyContent.linkUrl),
    [currentNotification?.link, bodyContent.linkUrl]
  );
  const claimId = useMemo(
    () => extractClaimId(currentNotification, bodyContent, rawMessage),
    [currentNotification, bodyContent, rawMessage]
  );
  const notificationSiteId = useMemo(
    () => getNotificationSiteId(currentNotification, siteId),
    [currentNotification, siteId]
  );

  const getNotificationType = (current: any) => {
    const typeValue = current?.type || current?.notificationType || current?.category;
    return typeof typeValue === 'string' ? typeValue.toLowerCase() : '';
  };
  const getRefreshNotificationAction = (current: any) => {
    const type = getNotificationType(current);
    const query = {
      includeTracking: true,
      ...(siteId ? { siteId } : {}),
    };

    if (type === FOLLOW_UP_NOTIFICATION_TYPE) {
      return fetchFollowUpNotificationsAction({
        ...query,
        type: FOLLOW_UP_NOTIFICATION_TYPE,
      });
    }

    if (type === STALLED_CLAIM_NOTIFICATION_TYPE) {
      return fetchStalledClaimNotificationsAction({
        ...query,
        type: STALLED_CLAIM_NOTIFICATION_TYPE,
      });
    }

    if (type === CLAIM_EMAIL_APPROVAL_NOTIFICATION_TYPE) {
      return fetchClaimEmailApprovalNotificationsAction({
        ...query,
        type: CLAIM_EMAIL_APPROVAL_NOTIFICATION_TYPE,
      });
    }

    if (type === CLAIM_CREATED_NOTIFICATION_TYPE) {
      return fetchClaimCreatedNotificationsAction({
        ...query,
        type: CLAIM_CREATED_NOTIFICATION_TYPE,
      });
    }

    return fetchNotificationsAction({
      ...query,
      type: WHATS_NEW_NOTIFICATION_TYPE,
    });
  };

  const markCurrentNotificationAsRead = () => {
    if (!currentNotification) return;
    const trackingRecord =
      currentNotification?.trackingRecords?.find(
        (record: any) => record && record.read === false
      ) || currentNotification?.trackingRecords?.[0];

    if (!trackingRecord?.id || trackingRecord.read === true) return;

    dispatch(
      markNotificationRead(
        {
          notificationId: currentNotification.id,
          trackingId: trackingRecord.id,
          read: true,
        },
        {
          callback: () => getRefreshNotificationAction(currentNotification),
        }
      )
    );
    hasMarkedRead.current = true;
  };

  useEffect(() => {
    if (!isOpen) {
      setQueueSnapshot([]);
      setCurrentIndex(0);
      hasMarkedRead.current = false;
      return;
    }

    if (!queueSnapshot.length && Array.isArray(notificationQueue) && notificationQueue.length) {
      setQueueSnapshot(notificationQueue.slice());
    } else if (!queueSnapshot.length && fallbackQueue.length) {
      setQueueSnapshot(fallbackQueue.slice());
    }
  }, [isOpen, notificationQueue, queueSnapshot.length, fallbackQueue]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setCurrentIndex(0);
    hasMarkedRead.current = false;
  }, [isOpen, queueKey]);

  useEffect(() => {
    hasMarkedRead.current = false;
  }, [currentNotification?.id]);

  useEffect(() => {
    if (!currentNotification || hasMarkedRead.current) return;
    markCurrentNotificationAsRead();
  }, [dispatch, currentNotification, siteId]);

  if (!currentNotification) return null;

  const finalizeClose = () => {
    if (onClose) {
      onClose(currentNotification?.id);
    }
    dispatch(hideDialog());
  };

  const handleBack = () => {
    if (hasMultiple && currentIndex > 0) {
      setCurrentIndex((prev) => Math.max(prev - 1, 0));
      hasMarkedRead.current = false;
    }
  };

  const handleNext = () => {
    if (hasMultiple && !isLast) {
      setCurrentIndex((prev) => Math.min(prev + 1, totalNotifications - 1));
      hasMarkedRead.current = false;
      return;
    }
    finalizeClose();
  };

  const handleClose = () => {
    if (hasMultiple && !isLast) {
      return;
    }
    finalizeClose();
  };

  const buttonLabel =
    hasMultiple && !isLast
      ? intl.formatMessage({ id: 'next', defaultMessage: 'Next' })
      : intl.formatMessage({ id: 'close', defaultMessage: 'Close' });
  const showBackButton = hasMultiple && currentIndex > 0;

  return (
    <ModalDialog
      title={
        currentNotification.title || intl.formatMessage({ id: 'update', defaultMessage: 'Update' })
      }
      isOpen={isOpen}
      close={handleClose}
      showFooter={false}
    >
      <div className="whats-new-detail">
        <div className="whats-new-modal-body">
          {messageHtml ? (
            <div
              className="whats-new-modal-content"
              dangerouslySetInnerHTML={{ __html: messageHtml }}
            />
          ) : (
            <p className="whats-new-modal-content">{followUpMessageParts.message}</p>
          )}
          {followUpNotesHtml && (
            <div className="whats-new-followup-notes">
              <p className="whats-new-followup-notes-title">Follow-up notes</p>
              <div
                className="whats-new-followup-notes-body"
                dangerouslySetInnerHTML={{ __html: followUpNotesHtml }}
              />
            </div>
          )}
          {imageSrc && (
            <div
              style={{ alignItems: 'center', display: 'flex', justifyContent: 'center' }}
              className="whats-new-modal-image"
            >
              <img
                style={{ maxWidth: '70%' }}
                src={imageSrc}
                alt={
                  currentNotification.title ||
                  intl.formatMessage({
                    id: 'notificationImage',
                    defaultMessage: 'Notification image',
                  })
                }
              />
            </div>
          )}

          {videoSrc ? (
            <div className="whats-new-modal-video">
              <video controls src={videoSrc} preload="metadata" />
            </div>
          ) : null}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '1rem',
          }}
        >
          <small style={{ color: '#888' }}>
            {currentNotification.createdAt
              ? moment(currentNotification.createdAt).format('LLL')
              : ''}
          </small>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {hasMultiple && (
              <div className="whats-new-count" style={{ marginRight: '1rem' }}>
                <small>
                  {intl.formatMessage(
                    { id: 'ofTotal', defaultMessage: '{current} of {total}' },
                    { current: currentIndex + 1, total: totalNotifications }
                  )}
                </small>
              </div>
            )}

            <div className="whats-new-actions">
              {showBackButton && (
                <button
                  type="button"
                  className="button mr-2"
                  onClick={handleBack}
                  style={{ fontSize: '14px' }}
                >
                  {intl.formatMessage({ id: 'back', defaultMessage: 'Back' })}
                </button>
              )}
              <ClaimNotificationActions
                notificationType={notificationType}
                claimId={claimId}
                notificationSiteId={notificationSiteId}
                siteExtId={coreSiteId}
                operatorGroup={operatorGroup}
                onMarkAsRead={markCurrentNotificationAsRead}
                onClose={finalizeClose}
              />
              <button
                type="button"
                className="button primary"
                onClick={handleNext}
                style={{ fontSize: '14px' }}
              >
                {buttonLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalDialog>
  );
};

export default WhatsNewNotificationDialog;
